import React, { useState, useEffect, useMemo } from 'react';
import { Gift, Phone, Mail, User, CheckCircle, AlertCircle, Copy, Check } from 'lucide-react';
import { RAFFLE_CLOSE_AT, RAFFLE_DRAW_DATE_LABEL } from '../data/constants';
import RaffleWheel from './RaffleWheel';

function formatPhone(raw) {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function useRefCode() {
  return useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    return ref ? ref.trim().toUpperCase() : null;
  }, []);
}

export default function RaffleEntryPage() {
  const refCode = useRefCode();
  const [form, setForm] = useState({ name: '', phone: '', email: '', smsConsent: false });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | duplicate | error
  const [errorMsg, setErrorMsg] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [copied, setCopied] = useState(false);

  // ?preview=1 lets the wheel/results page be checked anytime before the
  // real close date — harmless for real visitors since nobody links that.
  const closed = Date.now() > RAFFLE_CLOSE_AT.getTime()
    || new URLSearchParams(window.location.search).get('preview') === '1';

  // Result screens replace the form in place — without this, visitors who scrolled
  // down to submit stay scrolled down and the confirmation looks like a blank page.
  useEffect(() => {
    if (status === 'success' || status === 'duplicate') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [status]);

  function handlePhone(e) {
    setForm(f => ({ ...f, phone: formatPhone(e.target.value) }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const digits = form.phone.replace(/\D/g, '');
    if (!form.name.trim() || digits.length !== 10 || !form.email.trim()) return;

    setStatus('submitting');
    try {
      const resp = await fetch('/api/raffle-enter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone,
          email: form.email.trim().toLowerCase(),
          smsConsent: form.smsConsent,
          ref: refCode,
        }),
      });
      const data = await resp.json();

      if (data.status === 'duplicate') {
        setStatus('duplicate');
        return;
      }
      if (!resp.ok || data.status === 'error') {
        setErrorMsg(data.error || 'Something went wrong. Try again.');
        setStatus('error');
        return;
      }
      setReferralLink(data.referralLink || '');
      setStatus('success');
    } catch {
      setErrorMsg('Something went wrong. Try again.');
      setStatus('error');
    }
  }

  function copyLink() {
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  if (closed && status !== 'success' && status !== 'duplicate') {
    return <RaffleWheel />;
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center px-4 py-16">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-[#68caf1]/20 border-2 border-[#68caf1] flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#68caf1]" />
          </div>
          <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-3">You're In</p>
          <h1 className="text-3xl font-bold text-[#111827] mb-4">Entry Confirmed</h1>
          <p className="text-[#6B7280] text-base leading-relaxed mb-8">
            You're officially entered to win a free Alchemist Reset — a full interior deep clean, steam, and shampoo.
            Check your email for confirmation and full terms. Winner announced {RAFFLE_DRAW_DATE_LABEL}.
          </p>

          {referralLink && (
            <div className="bg-[#111827] rounded-2xl p-5 text-left mb-4">
              <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-2">Get Bonus Entries</p>
              <p className="text-white text-sm leading-relaxed mb-4">
                Share your link — every friend who enters through it earns you one extra entry.
              </p>
              <button
                onClick={copyLink}
                className="w-full flex items-center justify-between gap-3 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-left hover:bg-white/15 transition-colors"
              >
                <span className="text-[#68caf1] text-xs font-mono truncate">{referralLink.replace('https://', '')}</span>
                {copied ? <Check className="w-4 h-4 text-[#68caf1] flex-shrink-0" /> : <Copy className="w-4 h-4 text-gray-400 flex-shrink-0" />}
              </button>
            </div>
          )}

          <p className="text-xs text-[#9CA3AF] mt-6">
            Questions? Text Akeem at (301) 885-9767
          </p>
        </div>
      </div>
    );
  }

  if (status === 'duplicate') {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center px-4 py-16">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-amber-100 border-2 border-amber-400 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-amber-500" />
          </div>
          <p className="text-amber-500 text-xs font-bold uppercase tracking-widest mb-3">Already Entered</p>
          <h1 className="text-3xl font-bold text-[#111827] mb-4">You're Already In</h1>
          <p className="text-[#6B7280] text-base leading-relaxed">
            We already have an entry for that phone number. One entry per person — you're good to go. Good luck!
          </p>
          <p className="text-xs text-[#9CA3AF] mt-6">
            Questions? Text Akeem at (301) 885-9767
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center px-4 py-16">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-[#68caf1]/20 border-2 border-[#68caf1] flex items-center justify-center mx-auto mb-4">
            <Gift className="w-8 h-8 text-[#68caf1]" />
          </div>
          <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-2">Monarch Detailing</p>
          <h1 className="text-3xl font-bold text-[#111827] mb-3">Win a Free Detail</h1>
          <p className="text-[#6B7280] text-base leading-relaxed">
            Enter for a chance to win a free <strong className="text-[#111827]">Alchemist Reset</strong> — full steam, deep shampoo, windows, UV protect. Valued at $200+.
          </p>
        </div>

        {/* Recent work */}
        <div className="rounded-2xl overflow-hidden mb-6 relative aspect-[3/4]">
          <img
            src="/images/raffle-interior-front-seat.jpg"
            alt="Recent Monarch Detailing interior work"
            className="w-full h-full object-cover"
          />
          <span className="absolute bottom-3 left-3 bg-black/70 text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full">
            Recent Work
          </span>
        </div>

        {refCode && (
          <div className="bg-[#68caf1]/10 border border-[#68caf1]/30 rounded-xl p-3 mb-6 text-center">
            <p className="text-xs text-[#111827]">
              <strong>You were referred!</strong> Enter below and your friend gets a bonus entry.
            </p>
          </div>
        )}

        {/* Prize card */}
        <div className="bg-[#111827] rounded-2xl p-5 mb-6">
          <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-3">The Prize</p>
          <ul className="space-y-2">
            {[
              'Full interior steam clean',
              'Deep carpet & seat shampoo',
              'Streak-free windows',
              'UV protectant on dash',
              'We come to you — Southern MD',
            ].map(item => (
              <li key={item} className="flex items-center gap-2 text-sm text-white">
                <span className="w-1.5 h-1.5 rounded-full bg-[#68caf1] flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
          <p className="text-gray-400 text-xs mt-4">Entries close Fri, Sept 18 · Winner announced {RAFFLE_DRAW_DATE_LABEL}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4">
          <p className="text-xs font-bold text-[#111827] uppercase tracking-widest mb-2">Enter Your Info</p>

          <div>
            <label className="block text-xs font-semibold text-[#374151] mb-1.5">Full Name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Your name"
                required
                className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm text-[#111827] focus:outline-none focus:border-[#68caf1] transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#374151] mb-1.5">Phone Number</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="tel"
                inputMode="numeric"
                value={form.phone}
                onChange={handlePhone}
                placeholder="(301) 555-0100"
                required
                className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm text-[#111827] focus:outline-none focus:border-[#68caf1] transition-colors"
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">One entry per phone number</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#374151] mb-1.5">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="you@email.com"
                required
                className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm text-[#111827] focus:outline-none focus:border-[#68caf1] transition-colors"
              />
            </div>
          </div>

          {/* SMS consent checkbox — separate, unchecked by default, not required to enter */}
          <div className="border border-gray-100 rounded-xl p-4 bg-gray-50">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.smsConsent}
                onChange={e => setForm(f => ({ ...f, smsConsent: e.target.checked }))}
                className="mt-0.5 w-4 h-4 accent-[#68caf1] flex-shrink-0"
              />
              <span className="text-xs text-[#374151] leading-relaxed">
                <strong>Yes, send me exclusive text deals.</strong> Be first to hear about future raffles, seasonal specials, and last-minute openings. I agree to receive recurring automated marketing text messages from Monarch Detailing at the number above. Message frequency varies. Msg &amp; data rates may apply. Reply <strong>STOP</strong> to unsubscribe, <strong>HELP</strong> for help.
              </span>
            </label>
            <p className="text-xs text-gray-400 mt-2 ml-7">Checking this box is optional — it is not required to enter the raffle.</p>
          </div>

          {status === 'error' && (
            <p className="text-red-500 text-xs">{errorMsg}</p>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full bg-[#68caf1] text-black font-bold py-3.5 rounded-2xl hover:bg-[#5bb8db] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
          >
            {status === 'submitting' ? 'Entering...' : 'Enter to Win'}
          </button>

          <p className="text-xs text-center text-gray-400 leading-relaxed">
            No purchase necessary. Must be 18+. St. Mary's · Charles · Calvert County, MD.
            Winner selected by random drawing, weighted by total entries, and notified by phone and email.
          </p>
        </form>

        <p className="text-xs text-center text-[#9CA3AF] mt-6">
          Monarch Detailing · Southern Maryland · (301) 885-9767
        </p>
      </div>
    </div>
  );
}
